import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white border border-parchborder rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-dill transition-colors";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-ink/40 mt-1">{hint}</span>}
  </label>
);

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white border border-parchborder rounded-2xl p-6 space-y-4">
    <div>
      <h3 className="font-serif font-bold text-lg text-dill">{title}</h3>
      <p className="text-xs text-ink/50 mt-0.5">{desc}</p>
    </div>
    {children}
  </div>
);

export const SiteSections = () => {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [marqueeText, setMarqueeText] = useState("");

  useEffect(() => {
    api
      .get("/content")
      .then((r) => {
        setContent(r.data);
        setMarqueeText((r.data.marquee?.items || []).join("\n"));
      })
      .catch(() => toast.error("Could not load site content"));
  }, []);

  const setHero = (k) => (e) =>
    setContent((c) => ({ ...c, hero: { ...c.hero, [k]: e.target.value } }));
  const setFooter = (k) => (e) =>
    setContent((c) => ({ ...c, footer: { ...c.footer, [k]: e.target.value } }));
  const setManifesto = (k) => (e) =>
    setContent((c) => ({ ...c, manifesto: { ...c.manifesto, [k]: e.target.value } }));
  const setChapter = (i, k) => (e) =>
    setContent((c) => ({
      ...c,
      manifesto: {
        ...c.manifesto,
        chapters: c.manifesto.chapters.map((ch, idx) =>
          idx === i ? { ...ch, [k]: e.target.value } : ch
        ),
      },
    }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...content,
        marquee: {
          ...content.marquee,
          items: marqueeText.split("\n").map((s) => s.trim()).filter(Boolean),
        },
      };
      const { data } = await api.put("/admin/content", payload);
      setContent(data);
      setMarqueeText((data.marquee?.items || []).join("\n"));
      toast.success("Site sections updated — the shop now shows your new wording");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!content) {
    return (
      <div className="flex justify-center py-16" data-testid="sections-loading">
        <Loader2 className="w-6 h-6 animate-spin text-dill" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-sections-panel">
      <SectionCard title="Promo Banner" desc="A green bar pinned to the very top of your site. Flip it on for a promotion, off when it's over.">
        <div className="flex items-center justify-between bg-parchdeep/60 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-dill">Show banner</p>
            <p className="text-[11px] text-ink/50">Visitors see it as soon as you save</p>
          </div>
          <button
            onClick={() => setContent((c) => ({ ...c, banner: { ...c.banner, enabled: !c.banner?.enabled } }))}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${content.banner?.enabled ? "bg-dill" : "bg-ink/20"}`}
            aria-label="Toggle promo banner"
            data-testid="banner-toggle"
          >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${content.banner?.enabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
        <Field
          label="Banner text"
          hint='Example: "Enter CHIPPIES for free delivery! Available until September 10th" — pair it with a code from the Promos tab.'
        >
          <textarea
            rows={2}
            value={content.banner?.text || ""}
            onChange={(e) => setContent((c) => ({ ...c, banner: { ...c.banner, text: e.target.value } }))}
            placeholder="Enter CHIPPIES for free delivery! Available until September 10th"
            className={inputCls}
            data-testid="banner-text"
          />
        </Field>
      </SectionCard>

      <SectionCard title="Hero (top of page)" desc="The big welcome area visitors see first.">
        <Field label="Small top line">
          <input value={content.hero.eyebrow || ""} onChange={setHero("eyebrow")} className={inputCls} data-testid="section-hero-eyebrow" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Headline line 1">
            <input value={content.hero.line1 || ""} onChange={setHero("line1")} className={inputCls} data-testid="section-hero-line1" />
          </Field>
          <Field label="Headline line 2 (accent)">
            <input value={content.hero.line2 || ""} onChange={setHero("line2")} className={inputCls} data-testid="section-hero-line2" />
          </Field>
        </div>
        <Field label="Headline line 3">
          <input value={content.hero.line3 || ""} onChange={setHero("line3")} className={inputCls} data-testid="section-hero-line3" />
        </Field>
        <Field label="Intro paragraph">
          <textarea value={content.hero.subtext || ""} onChange={setHero("subtext")} rows={3} className={inputCls} data-testid="section-hero-subtext" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Main button label">
            <input value={content.hero.cta_primary || ""} onChange={setHero("cta_primary")} className={inputCls} data-testid="section-hero-cta-primary" />
          </Field>
          <Field label="Second button label">
            <input value={content.hero.cta_secondary || ""} onChange={setHero("cta_secondary")} className={inputCls} data-testid="section-hero-cta-secondary" />
          </Field>
        </div>
        <Field label="Small note under buttons">
          <input value={content.hero.footnote || ""} onChange={setHero("footnote")} className={inputCls} data-testid="section-hero-footnote" />
        </Field>
        <Field label="Hero photo URL" hint="Any image link works — it's compressed automatically when you save.">
          <input value={content.hero.image || ""} onChange={setHero("image")} className={inputCls} data-testid="section-hero-image" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Stamp center text">
            <input value={content.hero.stamp_center || ""} onChange={setHero("stamp_center")} className={inputCls} data-testid="section-hero-stamp-center" />
          </Field>
          <Field label="Stamp ring text">
            <input value={content.hero.stamp_ring || ""} onChange={setHero("stamp_ring")} className={inputCls} data-testid="section-hero-stamp-ring" />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Sliding Ribbon" desc="The slow-moving text strip under the hero. One phrase per line.">
        <Field label="Ribbon phrases (one per line)">
          <textarea
            value={marqueeText}
            onChange={(e) => setMarqueeText(e.target.value)}
            rows={5}
            className={inputCls}
            data-testid="section-marquee-items"
          />
        </Field>
      </SectionCard>

      <SectionCard title="Our Roots (3 chapters)" desc="The numbered story section about your process.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Small top line">
            <input value={content.manifesto.eyebrow || ""} onChange={setManifesto("eyebrow")} className={inputCls} data-testid="section-manifesto-eyebrow" />
          </Field>
          <Field label="Section heading">
            <input value={content.manifesto.title || ""} onChange={setManifesto("title")} className={inputCls} data-testid="section-manifesto-title" />
          </Field>
        </div>
        {(content.manifesto.chapters || []).map((ch, i) => (
          <div key={i} className="border border-parchborder rounded-xl p-4 space-y-3" data-testid={`section-chapter-${i}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra font-semibold">
              Chapter {i + 1}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Number">
                <input value={ch.number || ""} onChange={setChapter(i, "number")} className={inputCls} data-testid={`section-chapter-${i}-number`} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Title">
                  <input value={ch.title || ""} onChange={setChapter(i, "title")} className={inputCls} data-testid={`section-chapter-${i}-title`} />
                </Field>
              </div>
            </div>
            <Field label="Story text">
              <textarea value={ch.text || ""} onChange={setChapter(i, "text")} rows={2} className={inputCls} data-testid={`section-chapter-${i}-text`} />
            </Field>
            <Field label="Photo URL">
              <input value={ch.image || ""} onChange={setChapter(i, "image")} className={inputCls} data-testid={`section-chapter-${i}-image`} />
            </Field>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Footer" desc="Address, hours, and the big stamp text at the bottom.">
        <Field label="Short brand blurb">
          <textarea value={content.footer.blurb || ""} onChange={setFooter("blurb")} rows={2} className={inputCls} data-testid="section-footer-blurb" />
        </Field>
        <Field label="Big stamp text">
          <input value={content.footer.stamp || ""} onChange={setFooter("stamp")} className={inputCls} data-testid="section-footer-stamp" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Address line 1">
            <input value={content.footer.address1 || ""} onChange={setFooter("address1")} className={inputCls} data-testid="section-footer-address1" />
          </Field>
          <Field label="Address line 2">
            <input value={content.footer.address2 || ""} onChange={setFooter("address2")} className={inputCls} data-testid="section-footer-address2" />
          </Field>
          <Field label="Address line 3">
            <input value={content.footer.address3 || ""} onChange={setFooter("address3")} className={inputCls} data-testid="section-footer-address3" />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Hours line 1">
            <input value={content.footer.hours1 || ""} onChange={setFooter("hours1")} className={inputCls} data-testid="section-footer-hours1" />
          </Field>
          <Field label="Hours line 2">
            <input value={content.footer.hours2 || ""} onChange={setFooter("hours2")} className={inputCls} data-testid="section-footer-hours2" />
          </Field>
          <Field label="Hours line 3">
            <input value={content.footer.hours3 || ""} onChange={setFooter("hours3")} className={inputCls} data-testid="section-footer-hours3" />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Payment Options" desc="Where customers send their payment after ordering.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Apple Pay phone number">
            <input
              value={content.payment?.apple_pay || ""}
              onChange={(e) => setContent((c) => ({ ...c, payment: { ...c.payment, apple_pay: e.target.value } }))}
              placeholder="405-555-0123"
              className={inputCls}
              data-testid="section-payment-applepay"
            />
          </Field>
          <Field label="CashApp $cashtag">
            <input
              value={content.payment?.cashtag || ""}
              onChange={(e) => setContent((c) => ({ ...c, payment: { ...c.payment, cashtag: e.target.value } }))}
              placeholder="$YourCashTag"
              className={inputCls}
              data-testid="section-payment-cashtag"
            />
          </Field>
          <Field label="Venmo username (without @)">
            <input
              value={content.payment?.venmo || ""}
              onChange={(e) => setContent((c) => ({ ...c, payment: { ...c.payment, venmo: e.target.value } }))}
              placeholder="PickleMeOK"
              className={inputCls}
              data-testid="section-payment-venmo"
            />
          </Field>
        </div>
      </SectionCard>

      <button
        onClick={save}
        disabled={busy}
        className="bg-dill hover:bg-terra disabled:opacity-60 text-parchment font-bold rounded-full px-8 py-3 text-sm uppercase tracking-widest transition-colors flex items-center gap-2"
        data-testid="admin-sections-save-button"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save All Sections
      </button>
    </div>
  );
};
